import { useEffect, useRef, useState } from "react";
import { useAuth } from "@/_core/hooks/useAuth";
import { trpc } from "@/lib/trpc";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { openDaumPostcode } from "@/lib/daumPostcode";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { SitePageShell } from "@/components/SiteHeader";
import { useLocation } from "wouter";

declare global {
  interface Window {
    TossPayments?: any;
  }
}

export default function CheckoutV1Page() {
  const { isAuthenticated, user } = useAuth();
  const [, navigate] = useLocation();

  // Parse URL params
  const params = new URLSearchParams(window.location.search);
  const productCode = params.get("productCode") ?? "";
  const orientation = (params.get("orientation") ?? "portrait") as "landscape" | "portrait";
  const resolutionStatus = (params.get("resolutionStatus") ?? "good") as "good" | "fair" | "low";
  const serviceType = params.get("serviceType") ?? "photo";
  const fileKey = params.get("fileKey") ?? "";
  const imageUrl = params.get("imageUrl") ?? "";

  // Price query
  const priceQuery = trpc.orders.estimateByCode.useQuery(
    { productCode },
    { enabled: !!productCode },
  );
  const price = priceQuery.data?.price ?? 0;
  const productName = priceQuery.data?.product?.name ?? productCode;

  // Shipping form
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [postalCode, setPostalCode] = useState("");
  const [address1, setAddress1] = useState("");
  const [address2, setAddress2] = useState("");
  const [note, setNote] = useState("");

  const handleAddressSearch = () => {
    openDaumPostcode()
      .then(({ postalCode: pc, address }) => {
        setPostalCode(pc);
        setAddress1(address);
        document.getElementById("checkoutv1-address2")?.focus();
      })
      .catch(() => toast.error("주소 검색을 불러오지 못했습니다."));
  };

  // Create order mutation
  const createOrder = trpc.orders.createV1.useMutation();
  const confirmPayment = trpc.payments.confirm.useMutation();
  const [isPending, setIsPending] = useState(false);
  const tossLoadedRef = useRef(false);

  // 토스페이먼츠 SDK 로드 (Checkout.tsx와 동일한 방식)
  useEffect(() => {
    if (tossLoadedRef.current) return;
    const script = document.createElement("script");
    script.src = "https://js.tosspayments.com/v2/standard";
    script.async = true;
    script.onload = () => {
      tossLoadedRef.current = true;
    };
    document.head.appendChild(script);
    return () => {
      if (script.parentNode === document.head) {
        document.head.removeChild(script);
      }
    };
  }, []);

  // 토스 결제 성공 콜백 처리 (리다이렉트 후)
  useEffect(() => {
    const url = new URL(window.location.href);
    const paymentKey = url.searchParams.get("paymentKey");
    const tossOrderId = url.searchParams.get("orderId");
    const amount = url.searchParams.get("amount");
    const dbOrderId = url.searchParams.get("dbOrderId");

    if (paymentKey && tossOrderId && amount && dbOrderId) {
      setIsPending(true);
      confirmPayment
        .mutateAsync({
          orderId: Number(dbOrderId),
          paymentKey,
          amount: Number(amount),
        })
        .then((result) => {
          if (result.success) {
            navigate(`/order-complete?orderId=${result.orderId}`);
          }
        })
        .catch((err) => {
          toast.error(err.message || "결제 승인에 실패했습니다.");
          setIsPending(false);
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 결제 실패 파라미터 처리
  useEffect(() => {
    const url = new URL(window.location.href);
    if (url.searchParams.get("paymentFailed") === "true") {
      const code = url.searchParams.get("code") ?? "";
      const msg = url.searchParams.get("message") ?? "결제에 실패했습니다.";
      if (code !== "PAY_PROCESS_CANCELED") {
        toast.error(msg);
      }
      url.searchParams.delete("paymentFailed");
      url.searchParams.delete("code");
      url.searchParams.delete("message");
      window.history.replaceState({}, "", url.toString());
    }
  }, []);

  const handleSubmit = async () => {
    if (!name || !phone || !postalCode || !address1) {
      toast.error("배송 정보를 모두 입력해주세요.");
      return;
    }
    if (!fileKey) {
      toast.error("이미지 파일이 없습니다. 처음부터 다시 시도해주세요.");
      return;
    }

    setIsPending(true);

    try {
      // 1. 주문 생성
      const order = await createOrder.mutateAsync({
        originalFileKey: fileKey,
        printFileKey: fileKey, // V1: same file for now (oil painting will generate separate)
        imageUrl: imageUrl || `/manus-storage/${fileKey}`,
        productCode,
        orientation,
        resolutionStatus,
        recipient: {
          name,
          phone,
          postalCode,
          address1,
          address2: address2 || undefined,
          note: note || undefined,
        },
      });

      // 2. 토스페이먼츠 결제 위젯 실행 (Checkout.tsx와 동일한 방식)
      const clientKey = import.meta.env.VITE_TOSS_CLIENT_KEY || "test_ck_DnyRpQWGrNzgM2aqJ0ogrKwv1M9E";

      if (!window.TossPayments) {
        toast.error("결제 모듈을 불러오는 중입니다. 잠시 후 다시 시도해주세요.");
        setIsPending(false);
        return;
      }

      const tossPayments = window.TossPayments(clientKey);
      const payment = tossPayments.payment({ customerKey: `user_${user?.id ?? "guest"}` });

      const successUrl = new URL(window.location.href);
      successUrl.searchParams.set("dbOrderId", String(order.orderId));

      const failUrl = new URL(window.location.href);
      failUrl.searchParams.set("paymentFailed", "true");

      await payment.requestPayment({
        method: "CARD",
        amount: {
          currency: "KRW",
          value: order.amount,
        },
        orderId: order.orderNo,
        orderName: productName,
        customerName: user?.name || name,
        customerEmail: user?.email || undefined,
        successUrl: successUrl.toString(),
        failUrl: failUrl.toString(),
      });
    } catch (e: unknown) {
      const message = e instanceof Error ? e.message : "주문에 실패했습니다.";
      if (message !== "사용자가 결제를 취소했습니다" && message !== "USER_CANCEL") {
        toast.error(message);
      }
      setIsPending(false);
    }
  };

  if (!isAuthenticated) {
    return <SitePageShell><div className="container py-20 text-center text-muted-foreground">로그인이 필요합니다.</div></SitePageShell>;
  }

  const submitButton = (
    <Button
      size="lg"
      className="w-full text-base font-semibold"
      onClick={handleSubmit}
      disabled={isPending}
    >
      {isPending ? (
        <><Loader2 className="w-4 h-4 animate-spin mr-2" />결제 처리 중...</>
      ) : (
        `${price.toLocaleString()}원 결제하기`
      )}
    </Button>
  );

  return (
    <SitePageShell>
      <div className="container max-w-xl mx-auto py-6 md:py-10 px-4 pb-28 md:pb-10">
        <h1 className="font-serif text-2xl mb-6">결제하기</h1>

        {/* Order Summary + artwork preview */}
        <Card className="mb-6 overflow-hidden py-0">
          {imageUrl && (
            <img
              src={imageUrl}
              alt={productName}
              className="w-full aspect-[4/3] object-cover"
            />
          )}
          <CardContent className="p-4">
            <div className="font-serif text-sm text-primary mb-3">주문 요약</div>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">상품</span>
                <span className="font-medium">{productName}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">방향</span>
                <span>{orientation === "landscape" ? "가로형" : "세로형"}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">해상도</span>
                <span className={resolutionStatus === "good" ? "text-green-600" : resolutionStatus === "fair" ? "text-yellow-600" : "text-red-600"}>
                  {resolutionStatus === "good" ? "좋음" : resolutionStatus === "fair" ? "보통" : "낮음"}
                </span>
              </div>
              <div className="border-t border-border/60 pt-2 mt-2 flex justify-between items-baseline">
                <span className="font-medium">결제 금액</span>
                <span className="font-serif text-xl tabular-nums text-primary">{price.toLocaleString()}원</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Shipping Form */}
        <Card className="mb-6">
          <CardContent className="p-4 space-y-4">
            <div className="font-serif text-sm text-primary mb-1">배송 정보</div>
            <div className="space-y-3">
              <div>
                <Label className="text-xs">받는 분</Label>
                <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="홍길동" />
              </div>
              <div>
                <Label className="text-xs">연락처</Label>
                <Input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="010-1234-5678" />
              </div>
              <div>
                <Label className="text-xs">우편번호</Label>
                <div className="flex gap-2">
                  <Input value={postalCode} readOnly placeholder="주소 검색을 눌러주세요" />
                  <Button type="button" variant="outline" className="shrink-0" onClick={handleAddressSearch}>주소 검색</Button>
                </div>
              </div>
              <div>
                <Label className="text-xs">기본주소</Label>
                <Input value={address1} readOnly placeholder="주소 검색을 눌러주세요" />
              </div>
              <div>
                <Label className="text-xs">상세주소</Label>
                <Input id="checkoutv1-address2" value={address2} onChange={(e) => setAddress2(e.target.value)} placeholder="101동 202호" />
              </div>
              <div>
                <Label className="text-xs">배송 메모</Label>
                <Textarea value={note} onChange={(e) => setNote(e.target.value)} placeholder="부재 시 경비실에 맡겨주세요" rows={2} />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Desktop CTA (inline) */}
        <div className="hidden md:block">{submitButton}</div>
      </div>

      {/* Mobile CTA (sticky, stays reachable without scrolling to page end) */}
      <div className="fixed bottom-0 inset-x-0 md:hidden bg-background/95 backdrop-blur-md border-t border-border/60 p-3 pb-[calc(0.75rem+env(safe-area-inset-bottom))] z-40">
        {submitButton}
      </div>
    </SitePageShell>
  );
}
